import { apiRequestWithRetry } from "src/services/ClientSideAPIService";

// Deduplication: Track logged errors in this session
const loggedErrors = new Set();

// Common crawler user agents
const crawlerRegex =
  /(Screaming Frog|HeadlessChrome|Googlebot|bingbot|DuckDuckBot|Baiduspider|YandexBot|SemrushBot|AhrefsBot|Applebot|GoogleOther)/i;

const isCrawlerUA = (ua = "") => crawlerRegex.test(ua);

// Detect known third-party ad / tracking scripts
const isThirdPartyScriptError = (stack = "", message = "") =>
  /optable\.co|blogherads\.com|doubleclick\.net|googlesyndication\.com|cloudfront\.net|solutionshindsight\.net|privacymanager\.io|ats-wrapper\.privacymanager\.io/i.test(
    stack
  ) || /googletag\.cmd\.push|affiliate_tags_override|shemedia/i.test(message);

// Identify vendor name from stack
const getThirdPartyVendor = (stack = "", message = "") => {
  if (/optable\.co/i.test(stack)) return "optable";
  if (/blogherads\.com|shemedia/i.test(stack)) return "shemedia";
  if (/doubleclick\.net/i.test(stack)) return "doubleclick";
  if (/googlesyndication\.com/i.test(stack)) return "googlesyndication";
  if (/privacymanager\.io|ats-wrapper/i.test(stack)) return "privacy-manager";
  if (/solutionshindsight\.net/i.test(stack)) return "solutions-hindsight";
  if (/googletag\.cmd\.push/i.test(message)) return "google-gpt";
  return null;
};

// Detect browser-level network noise
const isNetworkError = (message = "", stack = "") =>
  !stack &&
  /NetworkError|Failed to fetch|Load failed|The network connection was lost|Failed to track Pageview/i.test(
    message
  );

// IMPORTANT: Detect Next.js / Webpack chunk load errors
const isChunkLoadError = (message = "", stack = "") =>
  /ChunkLoadError|Loading chunk \d+ failed/i.test(message) ||
  /ChunkLoadError/i.test(stack);

// Detect DOM Event or non-error objects mistakenly passed as errors
const isEventLikeObject = (err) =>
  err &&
  typeof err === "object" &&
  !(err instanceof Error) &&
  ("isTrusted" in err || err.constructor?.name === "Event");

// Detect internal SDK / bridge noise (string-only, no stack)
const isInternalSDKNoise = (message = "", stack = "") =>
  !stack &&
  /Object Not Found Matching Id|MethodName:|ParamCount:|Invalid call to runtime\.sendMessage|invalid origin/i.test(
    message
  );

// Detect browser / feature-flag diagnostic messages
const isBrowserFeatureNoise = (message = "", stack = "") =>
  !stack && /feature named .* was not found|webCompat/i.test(message);

// Detect DOM mutation race conditions (ads / vendors)
const isDomMutationNoise = (message = "", stack = "") =>
  /removeChild|insertBefore|The object can not be found here/i.test(message) ||
  /removeChild|insertBefore/i.test(stack);

export const logErrorToAPI = async (error, additionalData = {}) => {
  let errObj = error;

  // Normalize error objects
  if (
    error &&
    typeof error === "object" &&
    "error" in error &&
    error.error instanceof Error
  ) {
    errObj = error.error;
  }

  let message =
    typeof errObj?.message === "string"
      ? errObj.message
      : JSON.stringify(errObj);
  let stack = typeof errObj?.stack === "string" ? errObj.stack : "";
  const name =
    typeof errObj?.name === "string"
      ? errObj.name
      : errObj?.constructor?.name || "UnknownError";

  // Detect cross-origin "Script error."
  let crossOrigin = false;
  let severity = additionalData.severity || "error";

  if (message === "Script error." && (!stack || stack === "Script error.")) {
    crossOrigin = true;
    severity = "warn";
  }

  const vendor = getThirdPartyVendor(stack, message);
  const thirdParty = isThirdPartyScriptError(stack, message);
  const chunkError = isChunkLoadError(message, stack);
  const internalSDKNoise = isInternalSDKNoise(message, stack);
  const browserFeatureNoise = isBrowserFeatureNoise(message, stack);
  const domMutationNoise = isDomMutationNoise(message, stack);

  if (thirdParty) {
    severity = "warn";
  }

  // ---------------------------------
  // 🚫 SKIP NON-IMPORTANT ERRORS
  // ---------------------------------
  // ❗ EXCEPT ChunkLoadError
  if (!chunkError) {
    if (thirdParty) return;
    if (isNetworkError(message, stack)) return;
    if (crossOrigin) return;
    if (internalSDKNoise) return;
    if (browserFeatureNoise) return;
    if (domMutationNoise) return;
    if (isEventLikeObject(errObj)) return;
  }

  // Deduplication key
  const errorKey = `${additionalData.errorId || ""}|${message}|${stack}`;
  if (loggedErrors.has(errorKey)) return;
  loggedErrors.add(errorKey);

  // Domain extraction
  let domain = "server-side";
  if (typeof window !== "undefined" && window.location) {
    const hostname = window.location.hostname;
    const parts = hostname.split(".");
    domain = parts.length >= 2 ? parts[parts.length - 2] : hostname;
  }

  const userAgent =
    typeof navigator !== "undefined" ? navigator.userAgent : "server-side";
  const crawler = isCrawlerUA(userAgent);

  const errorData = {
    timestamp: new Date().toISOString(),
    message,
    stack,
    name,
    url: typeof window !== "undefined" ? window.location.href : "server-side",
    domain,
    userAgent,
    crawler,
    crossOrigin,
    severity: crawler ? "warn" : severity,
    vendor,
    ...additionalData,
  };

  try {
    await apiRequestWithRetry("log/errors/frontend", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(errorData),
    });
  } catch (apiErr) {
    console.error("Failed to send error log", apiErr);
  }
};

export const setupGlobalErrorHandling = () => {
  if (typeof window === "undefined") return;

  window.addEventListener("unhandledrejection", (event) => {
    event.preventDefault();
    logErrorToAPI(event.reason, { type: "unhandledrejection" });
  });

  //commenting out for now as this is logging all the errors due to the usage of ad blockers which are not relevant. Can uncomment if needed.
  //Refer the below ticket for more details:
  //https://openroadmedia.atlassian.net/browse/ORMTECH-3722

  // window.addEventListener("error", (event) => {
  //   event.preventDefault();
  //   const error = new Error(event.message);
  //   error.stack = event.error?.stack || event.message;
  //   logErrorToAPI(error, {
  //     type: "global",
  //     filename: event.filename,
  //     lineno: event.lineno,
  //     colno: event.colno,
  //   });
  // });
};
